import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const EXAMPLE_PROMPTS = {
  'text-to-game': [
    "A platformer where a robot collects energy crystals across 3 levels",
    "A top-down maze game with a wizard casting spells at enemies",
    "A space shooter with power-ups and boss battles every 5 waves",
  ],
  'prompt-to-mini-game': [
    "A quick reaction game where you tap falling stars before they disappear",
    "A memory card matching game with animal characters",
    "A simple brick-breaker with colorful power-ups",
  ],
  'text-to-animation': [
    "A dragon flying through clouds with fire breath animation",
    "A character doing a victory dance after winning a race",
    "A bouncing ball with realistic physics and shadow",
  ],
  'script-to-video': [
    "Scene: Hero enters dark forest. Owl hoots. Lightning flashes revealing a castle.",
    "Opening cinematic: Spaceship lands on alien planet. Crew steps out cautiously.",
    "Tutorial intro: Friendly robot explains game controls with animated arrows.",
  ],
  'music-to-video': [
    "Upbeat chiptune track with pixel art characters dancing in a neon city",
    "Epic orchestral score with a knight charging into battle",
    "Calm ambient music with floating islands and gentle waterfalls",
  ],
};

const MODE_CONFIG = {
  'text-to-game': {
    icon: 'Gamepad2',
    label: 'Text to Game',
    color: 'var(--color-primary)',
    placeholder: 'Describe the game mechanics, characters, and environment.',
    buttonText: 'Generate Game',
    generatingText: 'Generating your game...',
  },
  'prompt-to-mini-game': {
    icon: 'Zap',
    label: 'Mini Game',
    color: 'var(--color-secondary)',
    placeholder: 'Describe a quick arcade or puzzle mini game.',
    buttonText: 'Generate Mini Game',
    generatingText: 'Generating your mini game...',
  },
  'text-to-animation': {
    icon: 'Film',
    label: 'Animation',
    color: 'var(--color-accent)',
    placeholder: 'Describe the animated scene you want to generate.',
    buttonText: 'Generate Animation',
    generatingText: 'Generating your animation...',
  },
  'script-to-video': {
    icon: 'Video',
    label: 'Script to Video',
    color: '#10B981',
    placeholder: 'Paste your script or describe the video scene.',
    buttonText: 'Generate Video',
    generatingText: 'Generating your video...',
  },
  'music-to-video': {
    icon: 'Music',
    label: 'Music to Video',
    color: '#F43F5E',
    placeholder: 'Describe the style of visuals that should match your music.',
    buttonText: 'Generate Video',
    generatingText: 'Generating your music video...',
  },
};

export default function PromptInput({ mode, prompt, onPromptChange, onGenerate, isGenerating, isSafe }) {
  const [charCount, setCharCount] = useState(prompt?.length);
  const config = MODE_CONFIG?.[mode] || MODE_CONFIG?.['text-to-game'];
  const examples = EXAMPLE_PROMPTS?.[mode] || [];

  const handleChange = (e) => {
    const val = e?.target?.value;
    if (val?.length <= 500) {
      onPromptChange(val);
      setCharCount(val?.length);
    }
  };

  const handleExample = (ex) => {
    onPromptChange(ex);
    setCharCount(ex?.length);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Prompt textarea */}
      <div className="relative">
        <textarea
          value={prompt}
          onChange={handleChange}
          placeholder={config?.placeholder}
          rows={5}
          className="w-full resize-none rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] p-4 pr-16 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
          style={{ fontFamily: 'var(--font-body)', minHeight: 120 }}
          disabled={isGenerating}
          aria-label="Creation prompt"
        />
        <span
          className="absolute bottom-3 right-4 text-xs font-data"
          style={{ color: charCount > 450 ? 'var(--color-warning)' : 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}
        >
          {charCount}/500
        </span>
      </div>
      {/* Example prompts */}
      <div>
        <p className="text-xs font-caption mb-2" style={{ color: 'var(--color-muted-foreground)' }}>
          <Icon name="Lightbulb" size={12} className="inline mr-1" />
          Example prompts:
        </p>
        <div className="flex flex-wrap gap-2">
          {examples?.map((ex, i) => (
            <button
              key={i}
              onClick={() => handleExample(ex)}
              disabled={isGenerating}
              className="text-xs px-3 py-1.5 rounded-full border transition-all hover:opacity-80 disabled:opacity-40 text-left line-clamp-1 max-w-[220px]"
              style={{
                borderColor: config?.color,
                color: config?.color,
                background: `${config?.color}12`,
                fontFamily: 'var(--font-caption)',
              }}
              title={ex}
            >
              {ex?.length > 40 ? ex?.slice(0, 40) + '\u2026' : ex}
            </button>
          ))}
        </div>
      </div>
      {/* Safety warning */}
      {!isSafe && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <Icon name="ShieldAlert" size={16} color="var(--color-error)" />
          <p className="text-sm" style={{ color: 'var(--color-error)' }}>
            Your prompt contains content that violates our safety guidelines. Please revise it.
          </p>
        </div>
      )}
      {/* Generate button — dynamic text based on mode */}
      <Button
        variant="default"
        size="lg"
        fullWidth
        loading={isGenerating}
        iconName={isGenerating ? undefined : config?.icon}
        iconPosition="left"
        onClick={onGenerate}
        disabled={!prompt?.trim() || !isSafe || isGenerating}
      >
        {isGenerating ? config?.generatingText : config?.buttonText}
      </Button>
    </div>
  );
}